export const whatIsNasRadius = {
  id: 1,
  slug: 'what-is-nasradius',
  title: 'Introduction to NasRadius: ISP Billing Made Easy',
  imageURL: '/images/desktop-dashboard.png',
  content: `
Managing an ISP—whether you're running a hotspot in a neighborhood, providing PPPoE connections to homes, or offering custom static IPs—can quickly become overwhelming without the right tools.

**NasRadius** is built to simplify this. It's a powerful, all-in-one ISP billing platform designed specifically for Mikrotik-based networks in Kenya and beyond.

---

## 🌟 What is NasRadius?

**NasRadius** is a cloud-based billing and management system that automates the technical and financial aspects of running an ISP. Built for resellers, WISPs, and hotspot operators, it integrates seamlessly with Mikrotik routers and streamlines everything from user authentication to payment tracking.

Whether you're operating a single hotspot or scaling across estates, NasRadius gives you the control and insight to grow confidently.

---

## 🔧 Key Features of NasRadius

### ✅ Automated Billing
- Automatically deactivate users when plans expire or bundles run out.
- Reduce manual work and avoid billing errors.

### 💳 Integrated Payments
- Accept payments via MPesa and more.
- Instantly update customer accounts upon payment.

### 🌐 Hotspot Billing
- Create time- or data-based plans (e.g. 1GB/3 Days, 1 Hour Unlimited).
- Supports voucher codes and session tracking.

### 🔐 PPPoE Billing
- Handle long-term residential connections with username/password access.
- Disconnect sessions based on usage or expiry.

### 📡 Static IP Management
- Assign static IPs for customers needing port forwarding, CCTV access, or remote work.

### 👥 Customer Management
- Keep track of balances, top-ups, usage, and support history.

### 📲 SMS Alerts
- Automatically notify users of expired bundles or low balances.
- Improve customer retention and reduce service interruptions.

### 🌍 Remote Mikrotik Management
- Manage all Mikrotik routers from one dashboard.
- Push configurations, perform backups, and monitor sessions remotely.

---

## 💰 Flexible Pricing Plans

### 🔹 Hotspot Only — 4% of hotspot revenue / month
**Maximum: Ksh 3,000 per month** 🎯

You pay 4% of your hotspot revenue up to a maximum of Ksh 3,000 per month. You won't pay more than that, so the price scales with you as your business grows and won't eat into your profit.

Includes:
- Hotspot billing & analytics
- User session control
- Unlimited locations/mikrotiks included
- Mikrotik remote management

### 🔹 Hotspot + PPPoE + Static IP — 4% of hotspot revenue + Ksh 30 per Active PPPoE user / month
**Maximum: Ksh 3,000 per month** (for the 4% portion) + Ksh 30 per Active PPPoE user 🎯

You pay 4% of your hotspot revenue up to a maximum of Ksh 3,000 per month (you won't pay more than that for the percentage portion), plus Ksh 30 per Active PPPoE user. The price scales with you as your business grows and won't eat into your profit.

Everything in the Hotspot Only plan, plus:
- PPPoE user management
- Static IP assignment & control
- Unlimited locations/mikrotiks included
- Mikrotik remote management

> ⚠️ All plans are monthly and scale as you grow. The 4% hotspot revenue fee is capped at Ksh 3,000 per month, ensuring predictable costs even as your business scales.

---

## 💡 Who Is NasRadius For?

- Small and mid-size ISPs in Kenya
- Hotspot business owners
- Cyber cafes and estates reselling internet
- Network administrators managing multiple Mikrotik routers
- Entrepreneurs looking to build passive income with Wi-Fi

---

## 🧠 Why Use NasRadius?

Running your ISP with spreadsheets and WhatsApp confirmations might work for 5 users — but what about 50, 100, or 1,000?

NasRadius eliminates:
- Manual deactivations
- Forgotten expiries
- Payment reconciliation issues
- Router misconfigurations

You get:  
✅ Peace of mind  
✅ Better customer experience  
✅ Consistent revenue

---

## 🖥️ Dashboard Overview

Whether you’re on mobile or desktop, the dashboard offers:
- Real-time user activity
- Daily/weekly/monthly income tracking
- Voucher redemptions
- Router sync status

---

## 📌 Related Blog Posts

- [How to Resell Internet in Kenya](/blog/how-to-resell-internet-in-kenya)
- [Setting Up PPPoE Authentication on Mikrotik](/blog/setup-pppoe-authentication-on-mikrotik)
- [Best Mikrotik Routers for Internet Reselling](/blog/best-mikrotik-routers-for-internet-reselling)

---

## ✅ Final Thoughts

NasRadius was built with the challenges of local ISPs in mind. Whether you’re setting up your first hotspot or growing into multiple locations, this tool gives you everything you need to operate efficiently and scale fast.

---

## 🔗 Ready to Get Started?

👉 [**Try NasRadius**](https://nasradius.com) — Kenya's smart ISP billing platform.  
Automate your internet business today and watch it grow.
  `
};
